import {BrowserRouter as Router, Navigate, Route, Routes, useLocation} from 'react-router-dom';


import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import React, { lazy, Suspense } from 'react';
import Home from './components/pages/Home';
import About from './components/pages/About';
import NotFound from './components/pages/NotFound';
import Faq from './components/pages/Faq';
import Assessment from './components/pages/Assessment';
import LocateUs from './components/pages/LocateUs';
import Login from './components/pages/Login';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/notification/NotificationContext';
import ProtectedRoute from './components/ProtectecRoute';
import Users from './components/pages/admin/Users';

const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./components/pages/admin/Dashboard'));
const AdminOrders = lazy(() => import('./components/pages/admin/Orders'));
const AdminOrderLookup = lazy(() => import('./components/pages/admin/OrderLookup'));
const AdminOrderReport = lazy(() => import('./components/pages/admin/OrderReport'));
const AdminAssessmentReport = lazy(() => import('./components/pages/admin/AssessmentReport'));

const TAWK_SCRIPT_ID = 'tawk-to-script';
const TAWK_WIDGET_SRC = 'https://embed.tawk.to/68f8e58be75b6b1950dbc455/1j8641b4r';
const TAWK_READY_POLL_INTERVAL_MS = 200;
const TAWK_READY_POLL_MAX_ATTEMPTS = 50;
const isBlockedLayoutRoute = (pathname) => pathname?.startsWith('/admin') || pathname === '/users/login';
const isTawkApiReady = () => {
  const api = window.Tawk_API;
  return typeof api?.showWidget === 'function' && typeof api?.hideWidget === 'function';
};

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <Router>
          <TawkWidgetController />
          <div className="flex flex-col h-screen">
            <AppHeader />

            <main className="flex-grow">
              <Routes>
                <Route path='/' element={<Home />}></Route>
                <Route path='users/login' element={<Login/>} />

                <Route path='/admin' element={
                  <Suspense fallback={<div>Loading admin...</div>}>
                    <ProtectedRoute>
                      <AdminLayout><AdminLanding /></AdminLayout>
                    </ProtectedRoute>
                  </Suspense>
                } />

                <Route path='/admin/orders' element={
                  <Suspense fallback={<div>Loading admin...</div>}>
                    <ProtectedRoute>
                      <AdminLayout>
                        <DispatchRestricted>
                          <AdminOrders/>
                        </DispatchRestricted>
                      </AdminLayout>
                    </ProtectedRoute>
                  </Suspense>
                } />

                <Route path='/admin/orders/lookup' element={
                  <Suspense fallback={<div>Loading admin...</div>}>
                    <ProtectedRoute>
                      <AdminLayout><AdminOrderLookup/></AdminLayout>
                    </ProtectedRoute>
                  </Suspense>
                } />

                <Route path='/admin/reports/orders' element={
                  <Suspense fallback={<div>Loading report...</div>}>
                    <ProtectedRoute>
                      <AdminLayout>
                        <DispatchRestricted>
                          <AdminOrderReport/>
                        </DispatchRestricted>
                      </AdminLayout>
                    </ProtectedRoute>
                  </Suspense>
                } />

                <Route path='/admin/reports/assessments' element={
                  <Suspense fallback={<div>Loading report...</div>}>
                    <ProtectedRoute>
                      <AdminLayout>
                        <DispatchRestricted>
                          <AdminAssessmentReport/>
                        </DispatchRestricted>
                      </AdminLayout>
                    </ProtectedRoute>
                  </Suspense>
                } />

                <Route path='/admin/users' element={
                  <Suspense fallback={<div>Loading admin...</div>}>
                    <ProtectedRoute allowedRoles={["ADMIN"]}>
                      <AdminLayout><Users /></AdminLayout>
                    </ProtectedRoute>
                  </Suspense>
                } />

                {/* <Route path='/admin/settings' element={
                  <Suspense fallback={<div>Loading admin...</div>}>
                    <ProtectedRoute>
                      <AdminLayout><div>Settings page (stub)</div></AdminLayout>
                    </ProtectedRoute>
                  </Suspense>
                } /> */}

                <Route path='/about' element={<About />}></Route>
                <Route path='/notfound' element={<NotFound />}></Route>
                <Route path='/locateus' element={<LocateUs />}></Route>
                <Route path='/assessment' element={<Assessment />}></Route>
                <Route path='/faq' element={<Faq />}></Route>
                <Route path='/*' element={<NotFound />}></Route>
              </Routes>
            </main>
            <AppFooter />
          </div>
        </Router>
      </NotificationProvider>
    </AuthProvider>
  );
}

function TawkWidgetController() {
  const { pathname } = useLocation();
  const isBlockedRoute = isBlockedLayoutRoute(pathname);
  const [isReady, setIsReady] = React.useState(false);

  // Load script only on allowed routes so blocked routes never show launcher briefly.
  React.useEffect(() => {
    if (isBlockedRoute) return;

    const markReadyIfPossible = () => {
      if (isTawkApiReady()) {
        setIsReady(true);
        return true;
      }
      return false;
    };

    if (markReadyIfPossible()) return;

    const existingScript = document.getElementById(TAWK_SCRIPT_ID);
    if (existingScript) {
      window.Tawk_API = window.Tawk_API || {};
      const previousOnLoad = window.Tawk_API.onLoad;
      window.Tawk_API.onLoad = function (...args) {
        if (typeof previousOnLoad === 'function') {
          previousOnLoad.apply(this, args);
        }
        setIsReady(true);
      };

      const handleScriptLoad = () => {
        markReadyIfPossible();
      };

      const handleScriptError = () => {
        window.clearInterval(readyPoll);
      };

      existingScript.addEventListener('load', handleScriptLoad);
      existingScript.addEventListener('error', handleScriptError);

      // Fallback for edge cases where load has fired but API methods arrive slightly later.
      let attempts = 0;
      const readyPoll = window.setInterval(() => {
        attempts += 1;
        if (markReadyIfPossible()) {
          window.clearInterval(readyPoll);
        }
        if (attempts >= TAWK_READY_POLL_MAX_ATTEMPTS) {
          window.clearInterval(readyPoll);
        }
      }, TAWK_READY_POLL_INTERVAL_MS);

      return () => {
        existingScript.removeEventListener('load', handleScriptLoad);
        existingScript.removeEventListener('error', handleScriptError);
        window.clearInterval(readyPoll);
      };
    }

    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_LoadStart = new Date();

    // Register onLoad callback before script loads so Tawk calls it when ready
    const previousOnLoad = window.Tawk_API.onLoad;
    window.Tawk_API.onLoad = function () {
      if (typeof previousOnLoad === 'function') {
        previousOnLoad.apply(this, arguments);
      }
      setIsReady(true);
    };

    const script = document.createElement('script');
    script.id = TAWK_SCRIPT_ID;
    script.async = true;
    script.src = TAWK_WIDGET_SRC;
    script.charset = 'UTF-8';
    script.setAttribute('crossorigin', '*');
    script.addEventListener('error', () => {
      setIsReady(false);
    });
    document.body.appendChild(script);
  }, [isBlockedRoute]);

  // Use layout effect so hide/show is applied before paint on route changes.
  React.useLayoutEffect(() => {
    if (!isReady) return;

    const api = window.Tawk_API;
    if (!api) return;

    if (isBlockedRoute) {
      if (typeof api.hideWidget === 'function') api.hideWidget();
    } else {
      if (typeof api.showWidget === 'function') api.showWidget();
    }
  }, [isBlockedRoute, isReady]);

  return null;
}

function AdminLanding() {
  const { isDispatchOnlyUser } = useAuth();
  if (isDispatchOnlyUser()) {
    return <Navigate to="/admin/orders/lookup" replace />;
  }
  return <AdminDashboard />;
}

function DispatchRestricted({ children }) {
  const { isDispatchOnlyUser } = useAuth();
  if (isDispatchOnlyUser()) {
    return <Navigate to="/admin/orders/lookup" replace />;
  }
  return children;
}

function AppHeader() {
  const { pathname } = useLocation();
  const isBlockedRoute = isBlockedLayoutRoute(pathname);
  if (isBlockedRoute) return null;
  return (
    <>
      <Navbar />
      <div className="w-full h-1 bg-red-900"> .</div>
    </>
  );
}

function AppFooter() {
  const { pathname } = useLocation();
  const isBlockedRoute = isBlockedLayoutRoute(pathname);
  if (isBlockedRoute) return null;
  return <Footer />;
}

export default App;
